import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrencySymbol } from '../utils/currency';
import { FaTimes, FaSave, FaArrowLeft, FaArrowRight, FaTrash, FaMagic } from 'react-icons/fa';
import '../index.css';

const SUFFIX_TO_LABEL = {
  '10': '1', '15': '1.5', '20': '2', '25': '2.5', '30': '3',
  '35': '3.5', '40': '4', '45': '4.5', '50': '5', '55': '5.5', '60': '6',
};
const LABEL_TO_SUFFIX = Object.fromEntries(Object.entries(SUFFIX_TO_LABEL).map(([k, v]) => [v, k]));

const _getBackendUrl = () => {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocal
    ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
    : (import.meta.env.VITE_BACKEND_URL || window.location.origin);
};

const CustomLargeCardUser = ({
  profile,
  onClose,
  onSave,
  availableFlavours,
  availableFillings,
  availableSponges,
  combinations,
  coverCombinations,
  cakeChargeData = {},
  personServedData = {},
  userRole = 'Buyer',
  readOnly = false,
  isFromHome = false
}) => {
  const [currentTier, setCurrentTier] = useState(1);
  const [formData, setFormData] = useState({
    theme: '',
    eggless: false,
    glutenFree: false,
    quantity: '',
    ccost: '',
    images: [],
    cimage: '',
    tiers: [
      { tier: 1, cflavour: '', cfilling: '', sponge: '', cover: '', frosting: '' },
      { tier: 2, cflavour: '', cfilling: '', sponge: '', cover: '', frosting: '' },
      { tier: 3, cflavour: '', cfilling: '', sponge: '', cover: '', frosting: '' },
    ],
    multiTier: false,
    moreTier: false,
    tierCount: 1,
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [imagePreviews, setImagePreviews] = useState(['', '', '']);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [checkingCakePhoto, setCheckingCakePhoto] = useState(false);
  // Which photo slot the next file pick should overwrite (null = append a new one).
  const [replaceIndex, setReplaceIndex] = useState(null);
  const navigate = useNavigate();

  // State for filtered options, initialized as empty until data is available
  const [filteredFlavours, setFilteredFlavours] = useState([]);
  const [filteredFillings, setFilteredFillings] = useState([]);
  const [filteredSponges, setFilteredSponges] = useState([]);

  // Sort quantityOptions numerically — dynamic from cakeChargeData if available
  const isTierCake = formData.multiTier;
  const is3TierCake = formData.moreTier;
  const baseSegment = is3TierCake ? '3T' : (isTierCake ? 'T' : 'N');

  // Extract cover and frosting options from coverCombinations with sorting
  const coverOptions = Array.isArray(coverCombinations) 
    ? [...new Set(coverCombinations.map(combo => combo.cover).filter(Boolean))]
        .filter(c => !isTierCake || c.toLowerCase() !== 'fresh cream')
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    : [];
  const frostingOptions = Array.isArray(coverCombinations) 
    ? [...new Set(coverCombinations.map(combo => combo.frosting).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    : [];

  useEffect(() => {
  }, [coverCombinations]);

  const baseSuffix = (() => {
    const nums = Object.keys(cakeChargeData)
      .filter(k => new RegExp(`^${baseSegment}\\d+$`).test(k))
      .map(k => parseInt(k.slice(baseSegment.length)))
      .filter(n => !isNaN(n));
    return nums.length ? String(Math.min(...nums)) : (isTierCake ? '15' : '10');
  })();

  const quantityOptions = (() => {
    if (cakeChargeData && Object.keys(cakeChargeData).length > 0) {
      const suffixes = [...new Set(
        Object.keys(cakeChargeData)
          .filter(k => new RegExp(`^${baseSegment}\\d+$`).test(k))
          .map(k => k.slice(baseSegment.length))
      )].sort((a, b) => parseInt(a) - parseInt(b));
      return suffixes.map(s => SUFFIX_TO_LABEL[s]).filter(Boolean);
    }
    return ['0.75kg', '1kg', '1.5kg', '2kg', '2.5kg', '3kg'].sort((a, b) =>
      parseFloat(a) - parseFloat(b)
    );
  })();

  // Derive price segments from combinations for tier 1
  const tier1 = formData.tiers[0];
  const comboMatch = Array.isArray(combinations)
    ? combinations.find(c => c.flavour === tier1.cflavour && c.filling === tier1.cfilling)
      || combinations.find(c => c.flavour === tier1.cflavour)
    : null;
  const isSpecialFlavour = comboMatch?.priceSegment === 'S';

  const coverMatchCombo = Array.isArray(coverCombinations)
    ? coverCombinations.find(c => c.cover === tier1.cover)
    : null;
  const coverPriceSegment = coverMatchCombo?.priceSegment === 'B' ? 'B' : 'F';

  // Price helpers
  const baseAndDelta = (prefix, suffix) => {
    const base = parseFloat(cakeChargeData[prefix + baseSuffix] || 0);
    const delta = suffix !== baseSuffix ? parseFloat(cakeChargeData[prefix + suffix] || 0) : 0;
    return base + delta;
  };
  const flatSurcharge = (prefix, suffix) => parseFloat(cakeChargeData[prefix + suffix] || 0);

  const priceSuffix = LABEL_TO_SUFFIX[formData.quantity] || '';
  const costEstimate = (() => {
    if (!priceSuffix || Object.keys(cakeChargeData).length === 0) return null;

    // Original profile's price segments (fixed at save time) — used for the "saved" side
    const origTier1Flavour = profile?.tiers?.[0]?.cflavour || '';
    const origComboMatch = Array.isArray(combinations)
      ? combinations.find(c => c.flavour === origTier1Flavour && c.filling === (profile?.tiers?.[0]?.cfilling || ''))
        || combinations.find(c => c.flavour === origTier1Flavour)
      : null;
    const origIsSpecial = origComboMatch?.priceSegment === 'S';
    const origCoverMatch = Array.isArray(coverCombinations)
      ? coverCombinations.find(c => c.cover === (profile?.cover || ''))
      : null;
    const origHasB = !isTierCake && origCoverMatch?.priceSegment === 'B';

    // Case 1: isFromHome — anchor is profile.ccost (design price at base qty from home catalogue)
    // Mirrors HomeProfileCardLarge: ccost embeds origIsSpecial S-base (no glutenFree in home formula)
    // glutenFree is treated as an additive flat surcharge on top, same as Case 3
    if (isFromHome && profile?.ccost) {
      const anchor = parseFloat(String(profile.ccost).replace('€', '') || 0);
      if (!anchor) return null;
      const sBase = origIsSpecial ? parseFloat(cakeChargeData['S' + baseSuffix] || 0) : 0;
      const bBase = origHasB ? parseFloat(cakeChargeData['B' + baseSuffix] || 0) : 0;
      const nDelta = priceSuffix !== baseSuffix ? parseFloat(cakeChargeData[baseSegment + priceSuffix] || 0) : 0;
      const sFlat = isSpecialFlavour ? parseFloat(cakeChargeData['S' + priceSuffix] || 0) : 0;
      const gfFlat = formData.glutenFree ? parseFloat(cakeChargeData['S' + priceSuffix] || 0) : 0;
      const bFlat = (!isTierCake && coverPriceSegment === 'B') ? parseFloat(cakeChargeData['B' + priceSuffix] || 0) : 0;
      return (anchor - sBase - bBase + nDelta + sFlat + gfFlat + bFlat).toFixed(2);
    }

    // Case 2: saved custom profile — anchor is profile.costest (price at the previously saved qty)
    // Subtracts the exact surcharges baked into costest (orig segments, saved qty)
    // then adds the current surcharges at the new qty
    if (profile?.costest && profile?.quantity) {
      const anchor = parseFloat(String(profile.costest).replace('€', '') || 0);
      if (!anchor) return null;
      const savedSuffix = LABEL_TO_SUFFIX[String(profile.quantity).replace(/\s*kg$/i, '').trim()] || baseSuffix;
      const nDeltaSaved = savedSuffix !== baseSuffix ? parseFloat(cakeChargeData[baseSegment + savedSuffix] || 0) : 0;
      const sFlatSaved = origIsSpecial ? parseFloat(cakeChargeData['S' + savedSuffix] || 0) : 0;
      const gfFlatSaved = (profile?.glutenFree || false) ? parseFloat(cakeChargeData['S' + savedSuffix] || 0) : 0;
      const bFlatSaved = origHasB ? parseFloat(cakeChargeData['B' + savedSuffix] || 0) : 0;
      const nDeltaNew = priceSuffix !== baseSuffix ? parseFloat(cakeChargeData[baseSegment + priceSuffix] || 0) : 0;
      const sFlatNew = isSpecialFlavour ? parseFloat(cakeChargeData['S' + priceSuffix] || 0) : 0;
      const gfFlatNew = formData.glutenFree ? parseFloat(cakeChargeData['S' + priceSuffix] || 0) : 0;
      const bFlatNew = (!isTierCake && coverPriceSegment === 'B') ? parseFloat(cakeChargeData['B' + priceSuffix] || 0) : 0;
      return (anchor - nDeltaSaved - sFlatSaved - gfFlatSaved - bFlatSaved + nDeltaNew + sFlatNew + gfFlatNew + bFlatNew).toFixed(2);
    }

    // Case 3: new profile — standard formula, no design premium
    return (
      baseAndDelta(baseSegment, priceSuffix)
      + (isSpecialFlavour ? flatSurcharge('S', priceSuffix) : 0)
      + (formData.glutenFree ? flatSurcharge('S', priceSuffix) : 0)
      + (!isTierCake && coverPriceSegment === 'B' ? flatSurcharge('B', priceSuffix) : 0)
    ).toFixed(2);
  })();

  // Initialize form data based on profile
  useEffect(() => {
    if (profile) {
      const initialCost = profile.ccost ? parseFloat(profile.ccost.replace('€', '')) : null;
      const initialImages = (profile.images && profile.images.length > 0) ? profile.images : (profile.cimage ? [profile.cimage] : []);
      const paddedImages = [...initialImages, ...Array(3 - initialImages.length).fill('')];

      const tiersFromProfile = profile.tiers || [];
      const fullTiers = [
        { tier: 1, cflavour: '', cfilling: '', sponge: '', cover: profile.cover || '', frosting: profile.frosting || '' },
        { tier: 2, cflavour: '', cfilling: '', sponge: '', cover: '', frosting: '' },
        { tier: 3, cflavour: '', cfilling: '', sponge: '', cover: '', frosting: '' },
      ];

      tiersFromProfile.forEach((tierData, index) => {
        const uiTierIndex = index;
        if (uiTierIndex < fullTiers.length) {
          fullTiers[uiTierIndex] = {
            tier: uiTierIndex + 1,
            cflavour: tierData.cflavour || '',
            cfilling: tierData.cfilling || '',
            sponge: tierData.sponge || '',
            cover: tierData.cover || profile.cover || '',
            frosting: tierData.frosting || profile.frosting || '',
          };
        }
      });

      const validTiers = fullTiers.filter(t => t.cflavour);

      // Derive tier type from cshape (for Home-added profiles where tierCount=1 but shape is tier)
      const is3TierShape = Array.isArray(profile.cshape)
        ? profile.cshape.some(s => typeof s === 'string' && s.toLowerCase() === '3tier')
        : typeof profile.cshape === 'string' && profile.cshape.toLowerCase() === '3tier';
      const isTierShape = Array.isArray(profile.cshape)
        ? profile.cshape.some(s => typeof s === 'string' && s.toLowerCase().includes('tier'))
        : typeof profile.cshape === 'string' && profile.cshape.toLowerCase().includes('tier');

      const storedTierCount = profile.tierCount || validTiers.length || 1;
      const initIsMultiTier = storedTierCount > 1 || isTierShape;
      const initIsMoreTier = storedTierCount > 2 || is3TierShape;
      const initTierCount = initIsMoreTier ? Math.max(storedTierCount, 3) : initIsMultiTier ? Math.max(storedTierCount, 2) : storedTierCount;

      const initSegment = initIsMoreTier ? '3T' : initIsMultiTier ? 'T' : 'N';
      const getDefaultQty = () => {
        const nums = Object.keys(cakeChargeData)
          .filter(k => new RegExp(`^${initSegment}\\d+$`).test(k))
          .map(k => parseInt(k.slice(initSegment.length)))
          .filter(n => !isNaN(n));
        const minSuffix = nums.length ? String(Math.min(...nums)) : (initIsMultiTier ? '15' : '10');
        return SUFFIX_TO_LABEL[minSuffix] || (initIsMoreTier ? '3' : initIsMultiTier ? '1.5' : '1');
      };
      const initialData = {
        theme: profile.theme || '',
        eggless: profile.eggless || false,
        glutenFree: profile.glutenFree || false,
        quantity: isFromHome ? '' : (profile.quantity != null ? String(profile.quantity).replace(/\s*kg$/i, '').trim() : getDefaultQty()),
        ccost: isFromHome ? '' : (profile.ccost || ''),
        costest: isFromHome ? '' : (profile.costest || ''),
        images: initialImages,
        cimage: '',
        tiers: fullTiers,
        multiTier: initIsMultiTier,
        moreTier: initIsMoreTier,
        tierCount: initTierCount,
      };
      setFormData(initialData);
      setInitialFormData(initialData);
      setImagePreviews(paddedImages);
      setCurrentImageIndex(initialImages.length > 0 ? 0 : 0);
      setIsUpdating(!!(initialImages && initialImages.length > 0));
    } else {
      const initialData = {
        theme: '',
        eggless: false,
        glutenFree: false,
        quantity: '',
        ccost: '',
        images: [],
        cimage: '',
        tiers: [
          { tier: 1, cflavour: '', cfilling: '', sponge: 'Vanilla', cover: '', frosting: '' },
          { tier: 2, cflavour: '', cfilling: '', sponge: '', cover: '', frosting: '' },
          { tier: 3, cflavour: '', cfilling: '', sponge: '', cover: '', frosting: '' },
        ],
        multiTier: false,
        moreTier: false,
        tierCount: 1,
      };
      setFormData(initialData);
      setInitialFormData(initialData);
      setIsUpdating(false);
    }
  }, [profile]);

  // On mount: check if ImageStudio returned a modified image (and restore saved form state)
  useEffect(() => {
    const returned = sessionStorage.getItem('studio_returned_image');
    const savedForm = sessionStorage.getItem('studio_saved_form');
    const savedIndex = sessionStorage.getItem('studio_return_image_index');
    // Only consume the handoff when it came from THIS card. CustomLargeCardSuper writes
    // the same studio_returned_image/studio_saved_form keys but with a single `cimage`
    // string and no images[] array — and Custom.jsx can briefly render this card before
    // userRole resolves to 'Super'. Reading Super's payload here crashed on [...base.images]
    // AND cleared the keys, so the Super card never got its edited image back.
    // studio_return_image_index is written only by this card, so it identifies the origin.
    if (savedIndex === null) return;
    if (returned || savedForm) {
      const base = savedForm ? JSON.parse(savedForm) : formData;
      const idx = Math.max(0, Math.min(2, parseInt(savedIndex, 10) || 0));
      const newImages = Array.isArray(base?.images) ? [...base.images] : [];
      if (returned) newImages[idx] = returned;
      const newFormData = { ...base, images: newImages };
      setFormData(newFormData);
      const paddedImages = [...newImages, ...Array(3 - newImages.length).fill('')];
      setImagePreviews(paddedImages);
      setCurrentImageIndex(idx);
      // Deliberately DON'T touch isUpdating. It means "this profile already had saved
      // images" and is set from the profile above; coming back from the studio doesn't
      // change that. Forcing it true here dropped a 1-image card into the bare
      // single-image branch, which renders no carousel arrows and no upload input — so
      // the buyer lost the ability to add their 2nd and 3rd photos.
      sessionStorage.removeItem('studio_returned_image');
      sessionStorage.removeItem('studio_return_profile_id');
      sessionStorage.removeItem('studio_saved_form');
      sessionStorage.removeItem('studio_return_image_index');
      setHasChanges(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Correct quantity if cakeChargeData loaded after initial form setup (e.g. 3Tier default falls back to '1.5' which is not a 3T option)
  useEffect(() => {
    if (Object.keys(cakeChargeData).length === 0) return;
    setFormData(prev => {
      if (!prev.quantity) return prev; // keep empty for new/isFromHome cards until user selects
      const seg = prev.moreTier ? '3T' : prev.multiTier ? 'T' : 'N';
      const nums = Object.keys(cakeChargeData)
        .filter(k => new RegExp(`^${seg}\\d+$`).test(k))
        .map(k => parseInt(k.slice(seg.length)))
        .filter(n => !isNaN(n));
      if (!nums.length) return prev;
      const validOptions = nums.sort((a, b) => a - b).map(n => SUFFIX_TO_LABEL[String(n)]).filter(Boolean);
      if (validOptions.includes(prev.quantity)) return prev;
      return { ...prev, quantity: validOptions[0] };
    });
  }, [cakeChargeData]);

  const [baseCost, setBaseCost] = useState(null);

  useEffect(() => {
    setFilteredFlavours(availableFlavours ? [...availableFlavours].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })) : []);
    setFilteredFillings(availableFillings ? [...availableFillings].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })) : []);
    setFilteredSponges(availableSponges ? [...availableSponges].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })) : []);
  }, [availableFlavours, availableFillings, availableSponges]);

  const isInitialRender = useRef(true);

  useEffect(() => {

    const currentTierData = formData.tiers[currentTier - 1];
    const selectedFlavour = currentTierData.cflavour;

    if ((!isInitialRender.current) && (!combinations || combinations.length === 0)) {
      console.warn('No combinations available for filtering.');
      return;
    }

    const filterOptions = () => {
      let filteredFlavourOptions = availableFlavours 
        ? [...availableFlavours].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })) 
        : [];
      
      let filteredFillingOptions = [];
      let filteredSpongeOptions = [];

      if (selectedFlavour) {
        const filteredCombinations = combinations.filter(combo => combo.flavour === selectedFlavour);
        filteredFillingOptions = [...new Set(filteredCombinations.map(combo => combo.filling).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        filteredSpongeOptions = [...new Set(filteredCombinations.flatMap(combo => combo.sponge).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

        // Only clear fields if user changed the flavour (not on initial load)
        if (!isInitialRender.current) {
          if (currentTierData.cfilling && !filteredFillingOptions.includes(currentTierData.cfilling)) {
            handleTierChange(currentTier, 'cfilling', '');
          }
          if (currentTierData.sponge && !filteredSpongeOptions.includes(currentTierData.sponge)) {
            handleTierChange(currentTier, 'sponge', '');
          }
        }

        // Auto-fill if only one option and field is empty
        if (filteredFillingOptions.length === 1 && !currentTierData.cfilling) {
          handleTierChange(currentTier, 'cfilling', filteredFillingOptions[0]);
        }
        if (filteredSpongeOptions.length === 1 && !currentTierData.sponge) {
          handleTierChange(currentTier, 'sponge', filteredSpongeOptions[0]);
        }
      }

      // Only update state if values actually changed
      if (JSON.stringify(filteredFlavourOptions) !== JSON.stringify(filteredFlavours)) {
        setFilteredFlavours(filteredFlavourOptions);
      }
      if (JSON.stringify(filteredFillingOptions) !== JSON.stringify(filteredFillings)) {
        setFilteredFillings(filteredFillingOptions);
      }
      if (JSON.stringify(filteredSpongeOptions) !== JSON.stringify(filteredSponges)) {
        setFilteredSponges(filteredSpongeOptions);
      }
    };

    filterOptions();
    isInitialRender.current = false;
  }, [
    formData.tiers[currentTier - 1].cflavour, 
    formData.tiers[currentTier - 1].cfilling,
    formData.tiers[currentTier - 1].sponge,
    currentTier,
    combinations,
    availableFlavours,
    availableFillings,
    availableSponges,
    filteredFlavours,
    filteredFillings,
    filteredSponges
  ]);

  useEffect(() => {
  if (currentTier > 1) {
    const tier1Data = formData.tiers[0];
    if (tier1Data.cover || tier1Data.frosting) {
      setFormData(prev => {
        const updatedTiers = [...prev.tiers];
        updatedTiers[currentTier - 1] = {
          ...updatedTiers[currentTier - 1],
          cover: tier1Data.cover || '',
          frosting: tier1Data.frosting || '',
        };
        return { ...prev, tiers: updatedTiers };
      });
    }
  }
}, [currentTier, formData.tiers[0].cover, formData.tiers[0].frosting]);

  const deepEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) return false;

    const keys1 = Object.keys(obj1).filter(key => key !== 'costest');
    const keys2 = Object.keys(obj2).filter(key => key !== 'costest');

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
        if (obj1[key].length !== obj2[key].length) return false;
        for (let i = 0; i < obj1[key].length; i++) {
          if (!deepEqual(obj1[key][i], obj2[key][i])) return false;
        }
      } else if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
        if (!deepEqual(obj1[key], obj2[key])) return false;
      } else if (obj1[key] !== obj2[key]) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (initialFormData) {
      const hasFormChanges = !deepEqual(formData, initialFormData);
      setHasChanges(hasFormChanges);
    }
  }, [formData, initialFormData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      if (type === 'checkbox') {
        const updatedTiers = [...prev.tiers];
        if (name === 'multiTier' && !checked) {
          updatedTiers[1] = { tier: 2, cflavour: '', cfilling: '', sponge: '', cover: '', frosting: '' };
          updatedTiers[2] = { tier: 3, cflavour: '', cfilling: '', sponge: '', cover: '', frosting: '' };
          return { 
            ...prev, 
            [name]: checked,
            tiers: updatedTiers,
            moreTier: false,
            tierCount: 1,
            ccost: '', costest: '',
          };
        } else if (name === 'multiTier' && checked) {
          // Fresh Cream not allowed for tier cakes — reset cover so user must re-select
          if (updatedTiers[0].cover.toLowerCase() === 'fresh cream') {
            updatedTiers[0] = { ...updatedTiers[0], cover: '', frosting: '' };
          }
          return { ...prev, [name]: checked, tiers: updatedTiers, ccost: '', costest: '' };
        } else if (name === 'moreTier' && !checked) {
          updatedTiers[2] = { tier: 3, cflavour: '', cfilling: '', sponge: '', cover: '', frosting: '' };
          return { 
            ...prev, 
            [name]: checked,
            tiers: updatedTiers,
            tierCount: prev.multiTier ? 2 : 1,
            ccost: '', costest: '',
          };
        }
        // Reset cost for glutenFree / multiTier / moreTier toggles (not eggless)
        const costFields = (name !== 'quantity' && name !== 'eggless') ? { ccost: '', costest: '' } : {};
        return { ...prev, [name]: checked, ...costFields };
      }

      return { ...prev, [name]: value };
    });
  };

  const handleTierChange = (tier, field, value) => {
    setFormData(prev => {
      const updatedTiers = [...prev.tiers];
      const tierIndex = updatedTiers.findIndex(t => t.tier === tier);
      if (tierIndex !== -1) {
        updatedTiers[tierIndex] = {
          ...updatedTiers[tierIndex],
          [field]: value,
        };
        if (field === 'cover') {
          const selectedCover = value;
          const frostingsForCover = Array.isArray(coverCombinations)
            ? [...new Set(coverCombinations
                .filter(combo => combo.cover === selectedCover)
                .map(combo => combo.frosting)
                .filter(Boolean))]
            : [];
          const newFrosting = frostingsForCover.length === 1 ? frostingsForCover[0] : '';
          updatedTiers[tierIndex] = {
            ...updatedTiers[tierIndex],
            frosting: newFrosting,
          };
          if (tier === 1) {
            updatedTiers.forEach((tierData, index) => {
              if (index !== 0 && tierData.cflavour) {
                updatedTiers[index] = {
                  ...tierData,
                  cover: selectedCover,
                  frosting: newFrosting,
                };
              }
            });
          }
        }
      }
      // Reset cost estimate when any spec field changes
      return { ...prev, tiers: updatedTiers, ccost: '', costest: '' };
    });
  };

  const handleImageChange = (e) => {
    if (readOnly || isFromHome) return;

    const file = e.target.files[0];
    if (!file) return;

    const supportedFormats = ['image/jpeg', 'image/png', 'image/gif'];
    if (!supportedFormats.includes(file.type)) {
      setError('Unsupported image format. Please upload a JPEG, PNG, or GIF image.');
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Image size exceeds 2MB. Please upload a smaller image.');
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      if (img.width < 200 || img.height < 200) {
        setError('Image dimensions are too small. Recommended size is at least 200x200 pixels.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        // replaceIndex set → the user tapped an existing photo to swap it out, so overwrite
        // that slot instead of appending (appending was the only behaviour before, which is
        // why a photo could never be changed once added — only deleted and re-added).
        const isReplace = replaceIndex !== null && replaceIndex < formData.images.length;
        const newImages = isReplace
          ? formData.images.map((img, i) => (i === replaceIndex ? reader.result : img))
          : [...formData.images, reader.result].slice(0, 3);
        const paddedImages = [...newImages, ...Array(3 - newImages.length).fill('')];
        setFormData(prev => ({
          ...prev,
          images: newImages,
        }));
        setImagePreviews(paddedImages);
        setCurrentImageIndex(isReplace ? replaceIndex : newImages.length - 1);
        setReplaceIndex(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    };
    img.onerror = () => {
      setError('Invalid image file.');
    };
  };

  const handleDeleteImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    const paddedImages = [...newImages, ...Array(3 - newImages.length).fill('')];
    setFormData(prev => ({
      ...prev,
      images: newImages,
    }));
    setImagePreviews(paddedImages);
    if (currentImageIndex >= newImages.length && newImages.length > 0) {
      setCurrentImageIndex(newImages.length - 1);
    } else if (newImages.length === 0) {
      setCurrentImageIndex(0);
    }
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % 3);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + 3) % 3);
  };

  // Opens the currently-displayed photo in ImageStudio's AI tools -- but only once
  // Gemini confirms it's actually a cake photo (checked on tap, not on upload).
  // Shrink a photo before the cake check. The check only needs a yes/no answer, so a
  // small thumbnail is plenty — sending the full multi-megabyte photo just burns
  // bandwidth and runs into request-size limits. Falls back to the original if the
  // image can't be drawn to a canvas (e.g. a cross-origin URL without CORS headers).
  const shrinkForCheck = (src, maxDim = 768) => new Promise(resolve => {
    try {
      const img = new Image();
      if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
          const w = Math.max(1, Math.round(img.naturalWidth * scale));
          const h = Math.max(1, Math.round(img.naturalHeight * scale));
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(c.toDataURL('image/jpeg', 0.85));
        } catch { resolve(src); }   // tainted canvas → send as-is
      };
      img.onerror = () => resolve(src);
      img.src = src;
    } catch { resolve(src); }
  });

  // The wand runs a cake check before navigating, which takes a moment. Rendered from one
  // place (it appears in all three carousel branches) so the button and its progress text
  // can't drift apart. The caller keeps its own visibility guard; this only handles the
  // readOnly/isFromHome rules and the busy state.
  // Tap a photo to swap it for a different file. Only while the profile is UNSAVED —
  // once saved, the photo set is fixed (matching "once saved, no additional photo").
  const canReplacePhotos = !readOnly && !isFromHome && !isUpdating;
  const openReplacePicker = (index) => {
    if (!canReplacePhotos) return;
    setReplaceIndex(index);
    const input = document.getElementById('image-upload');
    if (input) { input.value = ''; input.click(); }   // clear so re-picking the same file still fires onChange
  };
  const replaceableImgProps = (index) => canReplacePhotos ? {
    onClick: () => openReplacePicker(index),
    style: { cursor: 'pointer' },
    title: 'Click to change this photo',
  } : {};

  const renderWandButton = () => (readOnly || isFromHome) ? null : (
    <>
      <button
        type="button"
        className="action-btn wand-btn"
        onClick={handleOpenImageStudio}
        disabled={checkingCakePhoto}
        title={checkingCakePhoto ? 'Checking the photo…' : 'Open in Image Studio (AI)'}
        style={{
          position: 'absolute', top: '5px', right: '5px',
          background: 'rgba(255, 255, 255, 0.88)', border: 'none',
          borderRadius: '50%', padding: '5px', cursor: checkingCakePhoto ? 'default' : 'pointer', color: '#666',
        }}
      >
        <FaMagic />
      </button>
      {checkingCakePhoto && (
        <span style={{
          position: 'absolute', top: '7px', right: '40px',
          background: 'rgba(0,0,0,0.72)', color: '#fff', fontSize: 11,
          padding: '3px 8px', borderRadius: 10, whiteSpace: 'nowrap', pointerEvents: 'none',
        }}>Checking photo…</span>
      )}
    </>
  );

  const handleOpenImageStudio = async () => {
    const image = formData.images[currentImageIndex];
    if (!image || checkingCakePhoto) return;
    setCheckingCakePhoto(true);
    setError(null);
    try {
      const thumb = await shrinkForCheck(image);
      const res = await fetch(`${_getBackendUrl()}/classify-cake-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: thumb }),
      });
      // A non-JSON body (e.g. Express's HTML 413 page when the photo is too large) leaves
      // data empty — surface the status so the real cause isn't hidden behind a generic message.
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error
          || (res.status === 413 ? 'That photo is too large to check — please use a smaller image.'
              : `Could not verify the photo (server responded ${res.status})`));
      }
      if (!data.isCake) {
        setError("This photo doesn't look like a cake. Please upload a photo of the cake.");
        return;
      }
      sessionStorage.setItem('studio_return_profile_id', profile?.id || '');
      sessionStorage.setItem('studio_saved_form', JSON.stringify(formData));
      sessionStorage.setItem('studio_return_image_index', String(currentImageIndex));
      navigate('/imagestudio', { state: { fromCustom: true, imageBase64: image, profileId: profile?.id || '' } });
    } catch (err) {
      setError(err.message);
    } finally {
      setCheckingCakePhoto(false);
    }
  };

  const validateTierFields = (tierIndex) => {
    const tierData = formData.tiers[tierIndex];

    // Check flavour first
    if (!tierData.cflavour) {
      return "Flavour is required.";
    }

    // Proceed with other validations only if flavour is filled
    if (!tierData.cfilling) {
      return "Filling is required.";
    }
    if (!tierData.sponge) {
      return "Sponge is required with a selected flavour.";
    }
    if (tierIndex === 0 && !tierData.cover) {
      return "Cover is required with a selected flavour.";
    }
    if (!formData.quantity) {
      return "Quantity is required.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly || !hasChanges) {
      return;
    }

    if (formData.images.length === 0) {
      setError('At least one image is required to save the form.');
      return;
    }
    let activeTiers = formData.tiers.filter(tier => tier.cflavour);
    let maxTier = 1;
    if (formData.multiTier) {
      maxTier = 2;
      if (formData.moreTier) {
        maxTier = 3;
      }
    }
    activeTiers = activeTiers.filter(tier => tier.tier <= maxTier);
   
    for (let i = 0; i < activeTiers.length; i++) {
      const tierIndex = formData.tiers.findIndex(t => t.tier === activeTiers[i].tier);
      const validationError = validateTierFields(tierIndex);
      if (validationError) {
        console.error('Validation failed for tier:', validationError);
        setError(validationError);
        return;
      }
    }

    if (activeTiers.length === 0) {
      setError('At least one tier must have a selected flavour.');
      return;
    }

    const firstActiveTier = activeTiers[0];
    const adjustedTiers = activeTiers.map((tier, index) => ({
      tier: index + 1,
      cflavour: tier.cflavour,
      cfilling: tier.cfilling,
      sponge: tier.sponge,
    }));

    const profileData = {
      cover: firstActiveTier.cover || null,
      frosting: firstActiveTier.frosting || null,
      eggless: formData.eggless,
      glutenFree: formData.glutenFree,
      quantity: formData.quantity || null,
      ccost: null,
      costest: costEstimate ? `€${costEstimate}` : (formData.costest || null),
      images: formData.images,
      tiers: adjustedTiers,
      tierCount: activeTiers.length,
    };

    if (formData.theme) {
      profileData.theme = formData.theme;
    }

    setError(null);
    setIsSaving(true);
    try {
      await onSave(profileData);
    } catch (err) {
      setError(`Failed to save: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNextTier = () => {
    if (isMultiTierDisabled() || currentTier >= 3) return;

    const validationError = validateTierFields(currentTier - 1);
    if (validationError) {
      setError(validationError);
      return;
    }

    const nextTier = currentTier + 1;
    setCurrentTier(nextTier);
    setFormData(prev => ({
      ...prev,
      tierCount: Math.max(prev.tierCount, nextTier),
    }));
  };

  const handlePrevTier = () => {
    if (currentTier === 1) return;

    setCurrentTier(prev => prev - 1);
  };

  const isMultiTierDisabled = () => {
    const quantityValue = formData.quantity ? parseFloat(formData.quantity.replace('kg', '')) : 0;

    // Condition 1: Profile has globalId and cShape includes 'Tier'
    const hasGlobalIdAndTierShape = profile && (profile.globalId != profile.id) && profile.cshape && (
      Array.isArray(profile.cshape)
        ? profile.cshape.some(shape => typeof shape === 'string' && shape.toLowerCase().includes('tier'))
        : typeof profile.cshape === 'string' && profile.cshape.toLowerCase().includes('tier')
    );

    // Condition 2: Profile has same globalId or empty globalId
    const noOrEmptyGlobalId = !profile || !profile.globalId || (profile.globalId == profile.id);

    // Quantity thresholds. A tier cake is built from half-size layers, so it only ever
    // lands on a .5 weight — whole-number sizes (1, 2, 3, 4kg) can't be multi-tier even
    // when they're above the minimum. Tolerance keeps the float compare safe.
    const isHalfSize = Math.abs((quantityValue % 1) - 0.5) < 0.001;
    const meetsQuantityRequirement = currentTier === 1
      ? !!formData.quantity && quantityValue >= 1.5 && isHalfSize
      : currentTier === 2
      ? !!formData.quantity && quantityValue >= 2.5 && isHalfSize
      : false;

    // Enable checkboxes if either condition is met
    const enableCheckboxes = 
      (hasGlobalIdAndTierShape && meetsQuantityRequirement) || 
      (noOrEmptyGlobalId && meetsQuantityRequirement);

    // Return true (disable) if neither condition is met, false (enable) if either is met
    return !enableCheckboxes;
  };

  const isForwardArrowDisabled = () => {
    if (currentTier >= 3) return true;
    if (currentTier === 1 && !formData.multiTier) return true;
    if (currentTier === 2 && !formData.moreTier) return true;
    return false;
  };

  const isMoreTierDisabled = () => {
    if (currentTier === 3) return true;
    return isMultiTierDisabled();
  };

  const isFillingSpongeDisabled = () => {
    const currentTierData = formData.tiers[currentTier - 1];
    const selectedFlavour = currentTierData.cflavour;
    return !selectedFlavour;
  };

  const isCoverFrostingReadOnly = (tier) => {
    if (readOnly) return true;
    if (hasTheme) return true;
    if (currentTier === 1) return false;
    return true;
  };

  const imageInstructions = 'Supported formats: JPEG, PNG, GIF. Max size: 2MB. Recommended dimensions: at least 200x200 pixels.';

  const hasTheme = !!formData.theme;
  const isFlavourReadOnlyForTier = (tier) => {
    if (!isUpdating || hasTheme) return false;
    if (formData.multiTier) {
      return tier === 1;
    }
    return true;
  };

  return (
    <div className="unified-profile-card unified-profile-user">
      {isSaving && (
        <div className="clcu-saving-bar-wrap">
          <div className="clcu-saving-bar-fill" />
        </div>
      )}
      {error && (
        <div className="error-popup">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Close</button>
        </div>
      )}
      <div className="profile-details unified-profile-user-details">
        <div className="clcs-top-bar">
          <button className="save-close-btn close-btn" onClick={onClose} disabled={isSaving}>
            <FaTimes />
          </button>
          {!readOnly && (
            <button 
              className="save-close-btn save-btn"
              onClick={handleSubmit}
              disabled={!hasChanges || isSaving}
            >
              <FaSave />
            </button>
          )}
        </div>
        <div className="form-group image-section">
          <div className="profile-image-section">
            <div className="image-carousel-container">
              {isUpdating ? (
                formData.images.length <= 1 ? (
                  formData.images.length > 0 && formData.images[0] ? (
                    <div className="image-carousel">
                      <div className="image-wrapper" style={{ position: 'relative' }}>
                        <img src={formData.images[0]} alt="Profile Preview" className="profile-image" {...replaceableImgProps(0)} />
                        {renderWandButton()}
                      </div>
                    </div>
                  ) : null
                ) : (
                  <>
                    <button 
                      type="button" 
                      className="carousel-arrow unified-profile-user-carousel-arrow left" 
                      onClick={handlePrevImage}
                      disabled={currentImageIndex === formData.images.length + 1 || formData.images.length == 0}
                    >
                      <FaArrowLeft />
                    </button>
                    <div className="image-carousel">
                      {formData.images.length > 0 && formData.images[currentImageIndex] ? (
                        <div className="image-wrapper" style={{ position: 'relative' }}>
                          <img src={formData.images[currentImageIndex]} alt="Profile Preview" className="profile-image" {...replaceableImgProps(currentImageIndex)} />
                          {!readOnly && !isFromHome && (
                            <button
                              type="button"
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteImage(currentImageIndex)}
                              style={{
                                position: 'absolute',
                                top: '5px',
                                left: '5px',
                                background: 'rgba(255, 255, 255, 0.88)',
                                border: 'none',
                                borderRadius: '50%',
                                padding: '5px',
                                cursor: 'pointer',
                                color: '#666',
                              }}
                            >
                              <FaTrash />
                            </button>
                          )}
                          {renderWandButton()}
                        </div>
                      ) : formData.images.length < 3 && !readOnly && !isFromHome ? (
                        <div
                          className="image-placeholder"
                          onClick={() => document.getElementById('image-upload').click()}
                        >
                          {imageInstructions}
                        </div>
                      ) : null}
                    </div>
                    <button 
                      type="button" 
                      className="carousel-arrow unified-profile-user-carousel-arrow right" 
                      onClick={handleNextImage}
                      disabled={currentImageIndex === formData.images.length || formData.images.length < 1}
                    >
                      <FaArrowRight />
                    </button>
                    {!readOnly && !isFromHome && formData.images.length < 3 && (
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    )}
                  </>
                )
              ) : (
                <>
                  <button 
                    type="button" 
                    className="carousel-arrow unified-profile-user-carousel-arrow left" 
                    onClick={handlePrevImage}
                    disabled={currentImageIndex === formData.images.length + 1 || formData.images.length == 0}
                  >
                    <FaArrowLeft />
                  </button>
                  <div className="image-carousel">
                    {imagePreviews[currentImageIndex] ? (
                      <div className="image-wrapper" style={{ position: 'relative' }}>
                        <img src={imagePreviews[currentImageIndex]} alt="Profile Preview" className="profile-image" {...replaceableImgProps(currentImageIndex)} />
                        {formData.images[currentImageIndex] && renderWandButton()}
                      </div>
                    ) : (
                      <div
                        className="image-placeholder"
                        onClick={() => !readOnly && !isFromHome && document.getElementById('image-upload').click()}
                      >
                        {imageInstructions}
                      </div>
                    )}
                  </div>
                  <button 
                    type="button" 
                    className="carousel-arrow unified-profile-user-carousel-arrow right" 
                    onClick={handleNextImage}
                    disabled={currentImageIndex === formData.images.length || formData.images.length < 1}
                  >
                    <FaArrowRight />
                  </button>
                  {!readOnly && !isFromHome && (
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          {formData.theme && (
            <div style={{display: 'flex', justifyContent: 'center', margin: '-15px 0 5px 0'}}>
              <span style={{fontSize: '14px'}}>{formData.theme}</span>
            </div>
          )}
          {formData.multiTier && (
            <>
              <span style={{ fontSize: '13px', marginBottom: '-10px' }}>{`Tier ${currentTier} Details`}</span>
            </>
          )}
          {(formData.theme || formData.multiTier) && (
            <div>
              <hr className="dotted-line" style={{marginBottom:'-5px'}}/>
            </div>
          )}
          <div className="form-group-row">
            <div className="form-group-item">
              <label>Flavour:</label>
              <div className="field-value">
                {readOnly || (isFromHome && currentTier === 1) ? (
                  <span>{formData.tiers[currentTier - 1].cflavour}</span>
                ) : (
                  <select
                    name={`cflavour-tier-${currentTier}`}
                    value={formData.tiers[currentTier - 1].cflavour || ''}
                    onChange={(e) => handleTierChange(currentTier, 'cflavour', e.target.value)}
                    required
                    disabled={readOnly || isFlavourReadOnlyForTier(currentTier)}
                  >
                    <option value="">Select</option>
                    {filteredFlavours.map(flavour => (
                      <option key={flavour} value={flavour}>
                        {flavour}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="form-group-item">
              <label>Cover:</label>
              <div className="field-value">
                {readOnly || isCoverFrostingReadOnly(currentTier) ? (
                  <span>{formData.tiers[currentTier - 1].cover || ''}</span>
                ) : (
                  <select
                    name={`cover-tier-${currentTier}`}
                    value={formData.tiers[currentTier - 1].cover || ''}
                    onChange={(e) => handleTierChange(currentTier, 'cover', e.target.value)}
                    required
                    disabled={readOnly || isCoverFrostingReadOnly(currentTier)}
                  >
                    <option value="">Select</option>
                    {coverOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
          <hr className="dotted-line" style={{margin:'-5px -5px 0 0'}}/>
          <div className="form-group-row">
            <div className="form-group-item">
              <label>Filling:</label>
              <div className="field-value">
                <select
                  name={`cfilling-tier-${currentTier}`}
                  value={formData.tiers[currentTier - 1].cfilling || ''}
                  onChange={(e) => handleTierChange(currentTier, 'cfilling', e.target.value)}
                  required
                  disabled={readOnly || isFillingSpongeDisabled()}
                >
                  <option value="">Select</option>
                  {formData.tiers[currentTier - 1].cfilling &&
                    !filteredFillings.includes(formData.tiers[currentTier - 1].cfilling) && (
                      <option value={formData.tiers[currentTier - 1].cfilling}>
                        {formData.tiers[currentTier - 1].cfilling}
                      </option>
                  )}
                  {filteredFillings.map(filling => (
                    <option key={filling} value={filling}>
                      {filling}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group-item">
              <label>Frosting:</label>
              <div className="field-value">
                {readOnly || isCoverFrostingReadOnly(currentTier) ? (
                  <span>{formData.tiers[currentTier - 1].frosting || ''}</span>
                ) : (() => {
                  const selectedCover = formData.tiers[currentTier - 1].cover;
                  const filteredFrostings = Array.isArray(coverCombinations)
                    ? [...new Set(coverCombinations
                        .filter(c => c.cover === selectedCover)
                        .map(c => c.frosting)
                        .filter(Boolean))]
                        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                    : [];
                  return filteredFrostings.length <= 1 ? (
                    <span>{formData.tiers[currentTier - 1].frosting || ''}</span>
                  ) : (
                    <select
                      name={`frosting-tier-${currentTier}`}
                      value={formData.tiers[currentTier - 1].frosting || ''}
                      onChange={(e) => handleTierChange(currentTier, 'frosting', e.target.value)}
                      required
                    >
                      <option value="">Select</option>
                      {filteredFrostings.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  );
                })()}
              </div>
            </div>
          </div>
          <hr className="dotted-line" style={{margin:'-1px -5px 0 0'}}/>
          <div className="form-group-row">
            <div className="form-group-item">
              <label>Sponge:</label>
              <div className="field-value">
                <select
                  name={`sponge-tier-${currentTier}`}
                  value={formData.tiers[currentTier - 1].sponge || ''}
                  onChange={(e) => handleTierChange(currentTier, 'sponge', e.target.value)}
                  required
                  disabled={readOnly || isFillingSpongeDisabled()}
                >
                  <option value="">Select</option>
                  {filteredSponges.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group-item checkbox-group" style={{margin:'4px 0 0 0'}}>
              <div className="form-group-item checkbox-group" style={{margin:'4px 0 0 0'}}>
                <div className="checkbox-item">
                  <label>Eggless:</label>
                  <div className="field-value">
                    {readOnly || currentTier > 1 ? (
                      <span>{formData.eggless ? 'Yes' : 'No'}</span>
                    ) : (
                      <input
                        type="checkbox"
                        name="eggless"
                        checked={formData.eggless}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    )}
                  </div>
                </div>
                <div className="checkbox-item">
                  <label>Gluten Free:</label>
                  <div className="field-value">
                    {readOnly || currentTier > 1 ? (
                      <span>{formData.glutenFree ? 'Yes' : 'No'}</span>
                    ) : (
                      <input
                        type="checkbox"
                        name="glutenFree"
                        checked={formData.glutenFree}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <>
            <hr className="dotted-line" style={{margin:'-1px -5px 0 0'}}/>
            <div className="form-group-row">
              <div className="form-group-item">
                <label>Quantity:</label>
                <div className="field-value">
                  {readOnly || currentTier > 1 ? (
                    <span>{formData.quantity ? `${formData.quantity}kg` : ""}</span>
                  ) : (
                    <select
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      required
                      disabled={readOnly || !formData.tiers[0]?.cflavour}
                      style={{ width: '80px', opacity: !formData.tiers[0]?.cflavour ? 0.5 : 1 }}
                    >
                      <option value="">Select</option>
                      {quantityOptions.map(option => (
                        <option key={option} value={option}>
                          {option}kg
                        </option>
                      ))}
                    </select>
                  )}
                  {priceSuffix && personServedData[baseSegment + priceSuffix] && (
                    <span style={{ fontSize: '12px', color: '#666', marginLeft: '6px' }}>
                      ({personServedData[baseSegment + priceSuffix]} Person)
                    </span>
                  )}
                </div>
              </div>
              {currentTier <= 3 && (costEstimate !== null || formData.costest || formData.ccost) && (
                <div className="form-group-item">
                  <label>Cost Est.:</label>
                  <div className="field-value">
                    <span>
                      {costEstimate !== null
                        ? `${getCurrencySymbol()}${costEstimate}`
                        : (formData.costest
                            ? `${getCurrencySymbol()}${String(formData.costest).replace(/^€|^EUR/i, '')}`
                            : `${getCurrencySymbol()}${formData.ccost}`)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <hr className="dotted-line" style={{margin:'-1px -5px 0 0'}}/>
            <div className="form-group-row">
              <div className="form-group-item tier-group">
                <label>{currentTier === 1 ? 'Multi-Tier:' : (currentTier === 2 ? 'More Tier:' : '')}</label>
                <div className="field-value tier-value">
                  {currentTier < 3 && (
                    <input
                      type="checkbox"
                      name={currentTier === 1 ? 'multiTier' : 'moreTier'}
                      style={{margin: '-15px 0 0 50px'}}
                      checked={currentTier === 1 ? formData.multiTier : formData.moreTier}
                      onChange={handleChange}
                      disabled={readOnly || isMoreTierDisabled() || (profile?.globalId && (currentTier === 1 ? formData.multiTier : formData.moreTier))}
                    />
                  )}
                </div>
              </div>
              <div className="form-group-item"></div>
              <div></div>
            </div>
            {currentTier <= 3 && (
              <div className="tier-nav-bottom">
                <button
                  type="button"
                  className="nav-arrow unified-profile-user-nav-arrow"
                  onClick={handlePrevTier}
                  disabled={currentTier === 1}
                >
                  <FaArrowLeft />
                </button>
                <button
                  type="button"
                  className="nav-arrow unified-profile-user-nav-arrow"
                  onClick={handleNextTier}
                  disabled={isForwardArrowDisabled()}
                >
                  <FaArrowRight />
                </button>
              </div>
            )}
          </>
        </form>
      </div>
    </div>
  );
};

export default React.memo(CustomLargeCardUser);